import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { WhatsAppEmbeddedSignupConfigResponse } from "@multi-chat/shared";
import { isApiError, isBackendUnavailable } from "../../api/client.js";
import { getEmbeddedSignupConfig } from "../../api/whatsapp-api.js";
import {
  buildEmbeddedSignupFbLoginOptions,
  createBrowserEmbeddedSignupSession,
  type EmbeddedSignupResult,
  type EmbeddedSignupSession,
} from "./embedded-signup-session.js";
import {
  attachMetaEmbeddedSignupMessageDiagnostics,
  beginMetaEmbeddedSignupAttempt,
  detectPopupBlockerHint,
  ensureMetaDebugWindowApi,
  getActiveMetaDebugCorrelationId,
  getFbSdkUrl,
  getMetaEmbeddedSignupElapsedMs,
  isMetaDebugEnabled,
  metaDebugLog,
  sanitizeMetaError,
  summarizeFbInitOptions,
  summarizeFbLoginCallback,
  summarizeFbLoginOptions,
} from "./meta-embedded-signup-debug.js";

interface UseMetaEmbeddedSignupResult {
  config: WhatsAppEmbeddedSignupConfigResponse | null;
  configError: string | null;
  loadConfig: (organizationId: number, signal: AbortSignal) => Promise<void>;
  launch: (onResult: (result: EmbeddedSignupResult) => void) => void;
  launching: boolean;
  launchError: string | null;
  clearLaunchError: () => void;
}

interface FbInitParams {
  appId: string;
  cookie: boolean;
  xfbml: boolean;
  version: string;
  status?: boolean;
  autoLogAppEvents?: boolean;
}

declare global {
  interface Window {
    FB?: {
      init: (params: FbInitParams) => void;
      login: (
        callback: (response: FbLoginResponse) => void,
        options: ReturnType<typeof buildEmbeddedSignupFbLoginOptions>,
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

interface FbLoginResponse {
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
    expiresIn?: number;
  };
  status?: string;
  error_code?: number | string;
  error_type?: string;
}

const FB_SDK_SCRIPT_ID = "multi-chat-fb-sdk";
const META_SDK_LOAD_FAILED = "META_SDK_LOAD_FAILED";
const META_SDK_INIT_FAILED = "META_SDK_INIT_FAILED";

function callFbInit(params: FbInitParams): void {
  metaDebugLog("META_FB_INIT", summarizeFbInitOptions(params));
  window.FB!.init(params);
}

function loadFbSdk(appId: string, version: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const sdkUrl = getFbSdkUrl();
    metaDebugLog("META_SDK_LOAD_STARTED", {
      startedAt: new Date().toISOString(),
      sdkUrl,
      fbAlreadyExisted: Boolean(window.FB),
    });

    const onSucceeded = (): void => {
      metaDebugLog("META_SDK_LOAD_SUCCEEDED", {
        hasWindowFb: Boolean(window.FB),
        hasFbInit: typeof window.FB?.init === "function",
        hasFbLogin: typeof window.FB?.login === "function",
      });
    };

    const onFailed = (error: unknown): void => {
      const sanitized = sanitizeMetaError(error);
      metaDebugLog("META_SDK_LOAD_FAILED", {
        errorName: sanitized.name,
        errorMessage: sanitized.message,
      });
    };

    if (window.FB) {
      try {
        callFbInit({ appId, cookie: false, xfbml: false, version });
        onSucceeded();
        resolve();
      } catch (e) {
        onFailed(e);
        reject(e);
      }
      return;
    }
    const existing = document.getElementById(FB_SDK_SCRIPT_ID);
    if (existing) {
      existing.addEventListener(
        "load",
        () => {
          if (window.FB) {
            callFbInit({ appId, cookie: false, xfbml: false, version });
            onSucceeded();
            resolve();
          }
        },
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.id = FB_SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = sdkUrl;
    script.onerror = () => {
      const error = new Error(META_SDK_LOAD_FAILED);
      onFailed(error);
      reject(error);
    };
    window.fbAsyncInit = () => {
      callFbInit({ appId, cookie: false, xfbml: false, version });
      onSucceeded();
      resolve();
    };
    document.head.appendChild(script);
  });
}

export function useMetaEmbeddedSignup(): UseMetaEmbeddedSignupResult {
  const { t } = useTranslation();
  const [config, setConfig] = useState<WhatsAppEmbeddedSignupConfigResponse | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const sdkPromiseRef = useRef<Promise<void> | null>(null);
  const organizationIdRef = useRef<number | null>(null);
  const sessionRef = useRef<EmbeddedSignupSession | null>(null);

  useEffect(() => {
    if (!isMetaDebugEnabled()) return;
    ensureMetaDebugWindowApi();
    attachMetaEmbeddedSignupMessageDiagnostics();
  }, []);

  useEffect(() => {
    return () => {
      sessionRef.current?.dispose();
      sessionRef.current = null;
    };
  }, []);

  const loadConfig = useCallback(
    async (organizationId: number, signal: AbortSignal) => {
      organizationIdRef.current = organizationId;
      setConfigError(null);
      try {
        const cfg = await getEmbeddedSignupConfig(organizationId, signal);
        if (signal.aborted) return;
        metaDebugLog("META_CONFIG_RECEIVED", {
          enabled: cfg.enabled,
          metaAppId: cfg.metaAppId,
          whatsappSignupConfigId: cfg.whatsappSignupConfigId,
          graphApiVersion: cfg.graphApiVersion,
          connectionState: cfg.connectionState,
          hasExistingConnection: cfg.hasExistingConnection,
          coexistenceOnboardingEnabled: cfg.coexistenceOnboardingEnabled === true,
        });
        setConfig(cfg);
      } catch (error) {
        if (signal.aborted) return;
        if (isApiError(error) && error.code === "FEATURE_DISABLED") {
          setConfigError("FEATURE_DISABLED");
          return;
        }
        if (isBackendUnavailable(error)) {
          setConfigError(t("whatsapp.unreachable"));
          return;
        }
        setConfigError(isApiError(error) ? error.message : t("whatsapp.unreachable"));
      }
    },
    [t],
  );

  const clearLaunchError = useCallback(() => setLaunchError(null), []);

  const launch = useCallback(
    (onResult: (result: EmbeddedSignupResult) => void) => {
      if (!config || !config.enabled) {
        setLaunchError(t("whatsapp.meta.notAvailable"));
        return;
      }

      sessionRef.current?.dispose();
      sessionRef.current = null;

      setLaunching(true);
      setLaunchError(null);

      const correlationId = beginMetaEmbeddedSignupAttempt();
      attachMetaEmbeddedSignupMessageDiagnostics();
      metaDebugLog(
        "META_EMBEDDED_SIGNUP_STARTED",
        {
          correlationId,
          pageOrigin: typeof window !== "undefined" ? window.location.origin : null,
          pagePathname: typeof window !== "undefined" ? window.location.pathname : null,
          sdkReady: Boolean(window.FB) && typeof window.FB?.login === "function",
          organizationId: organizationIdRef.current,
          popupBlockerHint: detectPopupBlockerHint(),
        },
        { correlationId },
      );

      const sdkPromise =
        sdkPromiseRef.current ??
        (sdkPromiseRef.current = loadFbSdk(config.metaAppId, config.graphApiVersion).catch((e) => {
          sdkPromiseRef.current = null;
          throw e;
        }));

      sdkPromise
        .then(() => {
          if (!window.FB) {
            throw new Error(META_SDK_INIT_FAILED);
          }

          const session = createBrowserEmbeddedSignupSession({
            onComplete: (result) => {
              sessionRef.current = null;
              setLaunching(false);
              onResult(result);
            },
            onCancel: () => {
              sessionRef.current = null;
              setLaunching(false);
              metaDebugLog("META_EMBEDDED_SIGNUP_CANCEL", {
                current_step: null,
                elapsedMs: getMetaEmbeddedSignupElapsedMs(),
                source: "session_cancel",
              });
              setLaunchError(t("whatsapp.meta.cancelled"));
            },
            onError: () => {
              sessionRef.current = null;
              setLaunching(false);
              setLaunchError(t("whatsapp.launchError"));
            },
            onTimeout: () => {
              sessionRef.current = null;
              setLaunching(false);
              setLaunchError(t("whatsapp.launchError"));
            },
            onMissingWaba: () => {
              sessionRef.current = null;
              setLaunching(false);
              setLaunchError(t("whatsapp.meta.missingWaba"));
            },
          });
          sessionRef.current = session;
          // Register the real WA_EMBEDDED_SIGNUP listener before FB.login.
          session.start();

          const loginOptions = buildEmbeddedSignupFbLoginOptions({
            whatsappSignupConfigId: config.whatsappSignupConfigId,
            coexistenceOnboardingEnabled: config.coexistenceOnboardingEnabled,
          });

          metaDebugLog(
            "META_FB_LOGIN_OPTIONS",
            summarizeFbLoginOptions(loginOptions as unknown as Record<string, unknown>),
            { correlationId, groupCollapsed: true },
          );

          window.FB.login(
            (response: FbLoginResponse) => {
              metaDebugLog(
                "META_FB_LOGIN_CALLBACK",
                {
                  ...summarizeFbLoginCallback(response),
                  correlationId: getActiveMetaDebugCorrelationId(),
                },
                { correlationId },
              );

              // Authorization code comes only from authResponse.code.
              session.acceptAuthorizationCode(response.authResponse?.code);
            },
            loginOptions,
          );
        })
        .catch((error) => {
          sessionRef.current?.dispose();
          sessionRef.current = null;
          setLaunching(false);
          const code = error instanceof Error ? error.message : "";
          if (code === META_SDK_LOAD_FAILED) {
            setLaunchError(t("whatsapp.meta.sdkLoadFailed"));
            return;
          }
          if (code === META_SDK_INIT_FAILED) {
            setLaunchError(t("whatsapp.meta.sdkInitFailed"));
            return;
          }
          setLaunchError(t("whatsapp.launchError"));
        });
    },
    [config, t],
  );

  return useMemo(
    () => ({
      config,
      configError,
      loadConfig,
      launch,
      launching,
      launchError,
      clearLaunchError,
    }),
    [config, configError, loadConfig, launch, launching, launchError, clearLaunchError],
  );
}
